import { View } from '@yleisradio/areena-types';
import { Container } from 'components/Container';
import { GridContainer, GridElement } from 'components/Grid';
import ViewMenus from 'components/View/ViewMenus/ViewMenus';
import { useAnalyticsForView } from 'hooks/analytics';
import { isListArray } from 'utils/content';
import { Channel } from './Channel/Channel';
import { SelectableChannel } from './Channel/SelectableChannel';
import styles from './Guide.module.scss';
import { GuideHeader } from './GuideHeader';
import { DatePicker } from './GuideHeader/DatePicker';
import { PastProgramsToggle } from './GuideHeader/PastProgramsToggle';
import { useShowPastPrograms } from './useShowPastPrograms';
import { useCallback, useEffect, useState } from 'react';
import { randomUUID } from 'utils/uuid';
import { SummaryViewEventParams } from 'services/snowplowAnalytics/contextGenerators';
import { guideViewEventGenerator } from 'services/snowplowAnalytics/viewEventGenerators';
import { findLabel } from 'utils/card';
import { useTrackSnowplowPageView } from 'hooks/useTrackSnowplowPageView';

interface GuideProps {
  /** Currently selected EPG date (YYYY-MM-DD) */
  currentDate: string;
  /** First available EPG date (YYYY-MM-DD) */
  firstDate: string;
  /** Last available EPG date (YYYY-MM-DD) */
  lastDate: string;
  isFutureDate: boolean;
  isToday: boolean;
  view: View;
}

export const Guide = (props: GuideProps) => {
  const [currentTime, setCurrentTime] = useState<number>(() => Date.now());

  const { tabs: channels = [], title, menus } = props.view;
  const [isShowPastProgramsToggledOn, setShowPastProgramsToggle] =
    useShowPastPrograms();

  useAnalyticsForView();
  useTrackSnowplowPageView(props.view.analytics);

  const areenaViewType = props.view.analytics?.pageload?.yle?.areena_view_type;
  const id = props.view.analytics?.pageload?.yle?.id;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!window.yleSnowplow) {
      throw new Error('Snowplow tracker is not available');
    }
    const viewUuid = randomUUID();
    const { event, context } = guideViewEventGenerator({
      viewUuid,
      date: id,
      title,
      viewMode: 'full',
      areenaViewType,
      areenaGuideContextPredicate: 'engagement_view_target',
    });

    window.yleSnowplow('trackSelfDescribingEvent', { event, context });
  }, [areenaViewType, id, title]);

  const sendSummaryViewEvent = useCallback(
    ({ viewUuid, card }: SummaryViewEventParams) => {
      if (!window.yleSnowplow) {
        throw new Error('Snowplow tracker is not available');
      }
      const itemId = findLabel(card.labels, 'itemId')?.raw;

      const isProgram = card.pointer?.type === 'program';

      const programId = isProgram ? itemId : undefined;
      const entityId = !isProgram ? itemId : undefined;

      const { event, context } = guideViewEventGenerator({
        viewUuid,
        date: id,
        title,
        programId,
        entityId,
        viewMode: 'summary',
        areenaViewType,
        areenaGuideContextPredicate: 'engagement_view_source',
      });

      window.yleSnowplow('trackSelfDescribingEvent', { event, context });
    },
    [areenaViewType, id, title]
  );

  const channelElements = channels.map((channel) => {
    const channelContent = channel.content;

    return (
      channelContent &&
      channelContent.length > 0 &&
      isListArray(channelContent) && (
        <GridElement
          key={channel.title}
          mobile={12}
          mobileLandscape={6}
          desktopSmall={6}
        >
          {channelContent.length > 1 && (
            <SelectableChannel
              isToday={props.isToday}
              isFutureDate={props.isFutureDate}
              showExpired={isShowPastProgramsToggledOn}
              channel={channel}
              currentTime={currentTime}
              sendSummaryViewEvent={sendSummaryViewEvent}
            />
          )}
          {channelContent.length === 1 && (
            <Channel
              isToday={props.isToday}
              isFutureDate={props.isFutureDate}
              showExpired={isShowPastProgramsToggledOn}
              title={channel.title}
              image={channel.image}
              channelContent={channelContent[0]!}
              currentTime={currentTime}
              sendSummaryViewEvent={sendSummaryViewEvent}
            />
          )}
        </GridElement>
      )
    );
  });

  return (
    <main id="maincontent" className={styles.root}>
      {menus ? <ViewMenus menus={menus} /> : null}
      <Container>
        <GuideHeader
          title={title}
          datePicker={
            <DatePicker
              currentDate={props.currentDate}
              firstDate={props.firstDate}
              lastDate={props.lastDate}
            />
          }
          pastProgramsToggle={
            props.isToday ? (
              <PastProgramsToggle
                checked={isShowPastProgramsToggledOn}
                toggle={() => {
                  setShowPastProgramsToggle(!isShowPastProgramsToggledOn);
                }}
              />
            ) : null
          }
        />
        <GridContainer gap="small">{channelElements}</GridContainer>
      </Container>
    </main>
  );
};
